import React, { useCallback, useEffect, useId, useRef } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ROLE_VARIANTS } from '../../../lib/roles';
import { GET_STARTED_CHOICES, MENU_NAV, currentProps } from './navItems';

// The page links under the audience rows: the destinations the rows already offer are not
// repeated (one link per destination in the whole panel).
const CHOICE_HREFS = new Set(GET_STARTED_CHOICES.map((c) => c.to));
const PAGE_LINKS = MENU_NAV.filter((item) => !CHOICE_HREFS.has(item.to));
import { accountGroups, greeting } from './accountLinks';
import { AccountLinkList, HEADER_PANEL, accountRowClass } from './headerParts';
import { ChooserList } from './GetStartedMenu';
import Eyebrow from '../Eyebrow';
import useDismiss from './useDismiss';

// The reference's .mobile-nav links: Manrope 500 26px / 1.35, 10px vertical padding.
const MENU_LINK = cn(
  'block py-2.5 font-numeral text-[1.625rem] font-medium leading-[1.35] tracking-[-0.03em] text-navy',
  'underline-offset-[6px] decoration-1 hover:underline aria-[current=page]:underline',
);

/**
 * The site header below 800px: the "Menu" toggle and the panel it opens under the bar
 * (the reference's dropdown panel, not a drawer). The panel opens with the three "Get
 * started" audience rows (ChooserList, the same as the header's chooser popover; upgrade
 * brief §2.1), then the reference's links except the three the rows already cover, then the account block: signed out
 * "Log in" and "Sign up" (AuthModal); signed in the name, the dashboards the NAV role
 * variant grants (as the old mobile drawer), Deal wallet (badge), My profile and Log out.
 *
 * Behaviour: the toggle carries aria-expanded / aria-controls; opening moves focus to the
 * first link (the first audience row); Escape closes and returns focus to the toggle; a click outside the header or
 * focus leaving it closes; every link closes it (SiteHeader also closes it on navigation
 * and locks the page scroll while it is open).
 *
 * While the session is still being checked (`loading`) the account block is an invisible,
 * inert placeholder of the signed-out block's size: no "Log in" flash for a signed-in visitor.
 *
 * Props: `pathname`, `user`, `isAuthenticated`, `loading`, `activeClaims`, `open` /
 * `onOpenChange`, `containerRef` (the header bar: clicks inside it do not dismiss),
 * `onLogin`, `onSignup`, `onLogout`.
 */
export default function MobileMenu({
  pathname, user, isAuthenticated, loading = false, activeClaims, open, onOpenChange, containerRef, onLogin, onSignup,
  onLogout,
}) {
  const toggleRef = useRef(null);
  const panelRef = useRef(null);
  const panelId = `site-menu-${useId().replace(/:/g, '')}`;
  const chooserLabelId = `${panelId}-start`;
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  useDismiss(open, close, containerRef, toggleRef);

  useEffect(() => {
    if (!open || !panelRef.current) return;
    const first = panelRef.current.querySelector('a[href], button:not([disabled])');
    if (first) first.focus();
  }, [open]);

  // The AuthModal takes focus from here; the toggle is where it comes back to.
  const auth = (fn) => () => {
    close();
    if (toggleRef.current) toggleRef.current.focus();
    fn();
  };

  return (
    <>
      <button
        ref={toggleRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => onOpenChange(!open)}
        className={cn(
          'flex min-h-11 shrink-0 cursor-pointer items-center gap-2.5 border-0 bg-transparent py-2.5 pl-2 pr-[3px] font-label text-xs text-navy',
          // Below 375px (at 16px text; wider with enlarged text) the reference's "Menu" word
          // pushed the toggle past the bar and the screen edge: the icon alone shows there
          // (a 44px target) and the word stays the button's name. Desktop: hidden (see
          // SiteHeader for the em breakpoints).
          '[@media(max-width:31.1875em)]:justify-center [@media(max-width:31.1875em)]:px-0',
          '[@media(min-width:22.5em)_and_(max-width:31.1875em)]:min-w-[44px] [@media(max-width:22.4375em)]:min-w-[40px]',
          '[@media(min-width:50em)]:hidden',
        )}
      >
        <span className="[@media(max-width:31.1875em)]:sr-only">Menu</span>
        <span aria-hidden="true" className="h-3 w-[1.1875rem] shrink-0 border-y border-current" />
      </button>

      <div
        id={panelId}
        ref={panelRef}
        hidden={!open}
        className={cn(
          HEADER_PANEL,
          'absolute left-[-1px] right-[-1px] top-full overflow-y-auto overscroll-contain',
          // the screen height below the bar (dvh where supported: the iOS URL bar)
          'max-h-[calc(100vh-5.5rem)] supports-[height:100dvh]:max-h-[calc(100dvh-5.5rem)]',
          'whitespace-normal px-[22px] py-7 [@media(min-width:50em)]:hidden',
        )}
      >
        <div className="mb-5 border-b border-brand-rule pb-4">
          <Eyebrow as="p" id={chooserLabelId} tone="muted" className="m-0 mb-1">Get started</Eyebrow>
          <ChooserList pathname={pathname} onNavigate={close} labelledBy={chooserLabelId} size="md" />
        </div>

        <nav aria-label="Menu">
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {PAGE_LINKS.map((item) => (
              <li key={item.to}>
                <Link to={item.to} onClick={close} className={MENU_LINK} {...currentProps(pathname, item.to)}>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="mt-5 border-t border-brand-rule pt-5">
          {loading ? (
            <div aria-hidden="true" data-account-placeholder="" className="invisible select-none">
              <span className={MENU_LINK}>Log in</span>
              <span className={MENU_LINK}>Sign up</span>
            </div>
          ) : isAuthenticated ? (
            <>
              <p className="m-0 mb-1 truncate font-label text-xs text-brand-muted">{greeting(user)}</p>
              <AccountLinkList
                groups={accountGroups(user, { variant: ROLE_VARIANTS.NAV, activeClaims })}
                pathname={pathname}
                onNavigate={close}
                size="md"
              />
              <div className="mt-3 border-t border-brand-rule pt-3">
                <button type="button" onClick={onLogout} className={cn(accountRowClass('md'), 'cursor-pointer border-0 px-0')}>
                  Log out
                </button>
              </div>
            </>
          ) : (
            <ul aria-label="Account" className="m-0 flex list-none flex-col gap-1.5 p-0">
              <li>
                <button type="button" onClick={auth(onLogin)} className={cn(MENU_LINK, 'w-full cursor-pointer border-0 bg-transparent px-0 text-left')}>
                  Log in
                </button>
              </li>
              <li>
                <button type="button" onClick={auth(onSignup)} className={cn(MENU_LINK, 'w-full cursor-pointer border-0 bg-transparent px-0 text-left')}>
                  Sign up
                </button>
              </li>
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
